import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaStickyNote,
  FaUserTie,
  FaMapSigns,
  FaTrain,
  FaBed,
  FaCommentDots,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import {
  updateActorEvent,
  updateActorAssignment,
  updateSupportStatus,
} from "../../../services/acteureventServices";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const SUPPORT_STATUS_OPTIONS = [
  { value: "", label: "Non renseigné" },
  { value: "pending", label: "En attente" },
  { value: "approved", label: "Validée" },
  { value: "rejected", label: "Refusée" },
];

const getResponseLabel = (status) => {
  switch (status) {
    case "accepted":
      return "Acceptée";
    case "rejected":
      return "Refusée";
    default:
      return "En attente";
  }
};

const formatTapisValue = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return "";
  if (/^\d+$/.test(cleaned)) return `Tapis ${cleaned}`;
  return cleaned;
};

const requiresTapis = (poste) =>
  ["Arbitre", "Commissaire Sportif"].includes(poste);

const NoteModal = ({ isOpen, onClose, actorEvent, fetchActors }) => {
  const [note, setNote] = useState("");
  const [observations, setObservations] = useState("");
  const [tapis, setTapis] = useState("");
  const [transportSupportStatus, setTransportSupportStatus] = useState("");
  const [accommodationSupportStatus, setAccommodationSupportStatus] =
    useState("");
  const [isSaving, setIsSaving] = useState(false);

  const titleId = "note-modal-title";
  const descriptionId = "note-modal-description";

  const canEditTapis = useMemo(
    () => requiresTapis(actorEvent?.poste),
    [actorEvent?.poste],
  );

  useEffect(() => {
    if (isOpen && actorEvent) {
      setNote(actorEvent.note || "");
      setObservations(actorEvent.observations || "");
      setTapis(actorEvent.tapis || "");
      setTransportSupportStatus(actorEvent.transport_support_status || "");
      setAccommodationSupportStatus(
        actorEvent.accommodation_support_status || "",
      );
    } else {
      setNote("");
      setObservations("");
      setTapis("");
      setTransportSupportStatus("");
      setAccommodationSupportStatus("");
    }
  }, [isOpen, actorEvent]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!actorEvent) return;

    try {
      setIsSaving(true);

      await updateActorEvent(actorEvent.acteur_id, actorEvent.event_id, {
        note,
        observations,
      });

      if (canEditTapis || actorEvent.tapis) {
        await updateActorAssignment(actorEvent.acteur_id, actorEvent.event_id, {
          tapis: formatTapisValue(tapis) || null,
        });
      }

      const supportPayload = {};

      if (actorEvent.need_transport_support === true) {
        supportPayload.transport_support_status =
          transportSupportStatus || null;
      }

      if (actorEvent.need_accommodation_support === true) {
        supportPayload.accommodation_support_status =
          accommodationSupportStatus || null;
      }

      if (Object.keys(supportPayload).length > 0) {
        await updateSupportStatus(
          actorEvent.acteur_id,
          actorEvent.event_id,
          supportPayload,
        );
      }

      toast.success("Informations mises à jour avec succès.");
      await fetchActors?.();
      onClose?.();
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des informations de l'acteur",
        error,
      );
      toast.error(
        "Erreur lors de la mise à jour des informations de l'acteur.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById={titleId}
      describedById={descriptionId}
      initialFocusSelector='input[name="note-field"]'
    >
      <ModalHeader
        id={titleId}
        title="Note & observations"
        subtitle="Suivi individuel de l’acteur sur l’événement"
        onClose={onClose}
      />

      <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
        <ModalBody id={descriptionId}>
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Acteur concerné
                  </p>

                  <div className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                    {actorEvent?.acteurLabel || "Acteur non renseigné"}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] sm:text-xs">
                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 font-medium text-blue-800 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUserTie />
                      Poste : {actorEvent?.poste || "Non renseigné"}
                    </span>

                    {actorEvent?.tapis && (
                      <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 font-medium text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                        <FaMapSigns />
                        {formatTapisValue(actorEvent.tapis)}
                      </span>
                    )}

                    {actorEvent?.response_status && (
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                        Réponse : {getResponseLabel(actorEvent.response_status)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
                  <FaStickyNote />
                </div>
              </div>
            </div>

            {actorEvent?.response_reason && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-800 dark:text-rose-300">
                  <FaCommentDots />
                  Motif du refus
                </div>
                <div className="mt-2 whitespace-pre-line text-sm text-slate-800 dark:text-slate-100">
                  {actorEvent.response_reason}
                </div>
              </div>
            )}

            {canEditTapis && (
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Tapis
                  </label>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Arbitres et commissaires sportifs
                  </span>
                </div>

                <input
                  type="text"
                  value={tapis}
                  onChange={(e) => setTapis(e.target.value)}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  placeholder="Ex : 1, 2, 3 ou Tapis 1"
                />
              </div>
            )}

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Note
                </label>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Ex : 1 à 5, A / B / C…
                </span>
              </div>

              <input
                data-autofocus
                name="note-field"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                placeholder="Saisir une note ou laisser vide"
              />
            </div>

            {(actorEvent?.need_transport_support === true ||
              actorEvent?.need_accommodation_support === true ||
              actorEvent?.support_request_comment) && (
              <div className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
                <div>
                  <p className="text-sm font-semibold text-sky-800 dark:text-sky-300">
                    Demandes de prise en charge
                  </p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    Vous pouvez suivre ici les demandes transport / hébergement
                    formulées par l’acteur.
                  </p>
                </div>

                {actorEvent?.need_transport_support === true && (
                  <div>
                    <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      <FaTrain className="text-sky-600" />
                      Statut transport
                    </label>
                    <select
                      value={transportSupportStatus}
                      onChange={(e) =>
                        setTransportSupportStatus(e.target.value)
                      }
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    >
                      {SUPPORT_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {actorEvent?.need_accommodation_support === true && (
                  <div>
                    <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      <FaBed className="text-violet-600" />
                      Statut hébergement
                    </label>
                    <select
                      value={accommodationSupportStatus}
                      onChange={(e) =>
                        setAccommodationSupportStatus(e.target.value)
                      }
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    >
                      {SUPPORT_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {actorEvent?.support_request_comment && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Commentaire de l’acteur
                    </div>
                    <div className="whitespace-pre-line">
                      {actorEvent.support_request_comment}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Observations
                </label>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Comportement, points forts, axes de progrès…
                </span>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                <ReactQuill
                  value={observations}
                  onChange={setObservations}
                  theme="snow"
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Rédigez vos observations..."
                  className="bg-white text-slate-900 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                Ces informations servent au suivi interne de l’acteur sur cet
                événement.
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={onClose}
            type="button"
            className="!rounded-2xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
            text="Fermer"
            disabled={isSaving}
          />

          <Button
            onClick={handleSave}
            type="submit"
            className="!rounded-2xl"
            text={isSaving ? "Enregistrement..." : "Enregistrer"}
            icon={FaSave}
            disabled={isSaving || !actorEvent}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NoteModal;
